import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { IAsset } from '../types/common';
import {
  AssetProtocolDeleteAssetRequest,
  AssetProtocolGetAssetImagesResponse,
  AssetProtocolGetAssetListRequest,
  AssetProtocolGetAssetListResponseAsset,
  AssetProtocolGetAssetRequest,
  AssetProtocolGetAssetResponse,
  AssetProtocolUpdateAssetRequest,
} from '../swagger/data-contracts';
import { queryClient } from '../index';
import { assetApi, assetMultipartApi } from './';

export const QueryKeyAsset = 'asset';
const queryOptions = { refetchOnWindowFocus: false };

export const useDeleteAssetMutation = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  AssetProtocolDeleteAssetRequest,
  unknown
> => {
  return useMutation(assetApi.deleteAssetCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyAsset, 'get_all_asset']);
      queryClient.invalidateQueries([QueryKeyAsset, 'get_asset_list']);
    },
  });
};

export const useCreateAssetMutation = (): UseMutationResult<AxiosResponse<void>, unknown, any, unknown> => {
  return useMutation(assetMultipartApi.createAssetCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyAsset, 'get_all_asset']);
      queryClient.invalidateQueries([QueryKeyAsset, 'get_asset_list']);
    },
  });
};

export const useUpdateAssetMutation = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  AssetProtocolUpdateAssetRequest,
  unknown
> => {
  return useMutation(assetApi.updateAssetCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyAsset, 'get_all_asset']);
      queryClient.invalidateQueries([QueryKeyAsset, 'get_asset_list']);
    },
  });
};

export const useGetAsset = (request: AssetProtocolGetAssetRequest): UseQueryResult<AssetProtocolGetAssetResponse> => {
  return useQuery(
    [QueryKeyAsset, 'get_asset', request],
    async () => {
      const { data } = await assetApi.getAssetCreate(request);
      return data;
    },
    queryOptions
  );
};

export const useGetAssetList = (
  request: AssetProtocolGetAssetListRequest
): UseQueryResult<AssetProtocolGetAssetListResponseAsset[]> => {
  return useQuery(
    [QueryKeyAsset, 'get_asset_list', request],
    async () => {
      const { data } = await assetApi.getAssetListCreate(request);
      return data.assets;
    },
    queryOptions
  );
};

export const useGetAllAssetList = (): UseQueryResult<IAsset[]> => {
  return useQuery(
    [QueryKeyAsset, 'get_all_asset'],
    async () => {
      const { data } = await assetApi.getAllAssetListList();
      return data.assets.map((asset) => ({ assetId: asset.id, assetName: asset.name }));
    },
    queryOptions
  );
};

export const useGetAssetImagesList = (): UseQueryResult<AssetProtocolGetAssetImagesResponse> => {
  return useQuery(
    [QueryKeyAsset, 'get_asset_image_list'],
    async () => {
      const { data } = await assetApi.getAssetImagesList();
      return data;
    },
    queryOptions
  );
};
